import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import EditLocationAltOutlinedIcon from "@mui/icons-material/EditLocationAltOutlined";
import { toast } from "react-toastify";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import {
  useDeleteVisaDocumentTemplate,
  useUploadVisaDocumentTemplate,
  useVisaDocumentTemplates,
} from "utils/visaDocumentService";
import VisaTemplateFieldsDialog from "./VisaTemplateFieldsDialog";

const VisaDocumentTemplateManagerDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const canCreate = usePermission(P.VISA_DOCUMENT.CREATE);
  const canDelete = usePermission(P.VISA_DOCUMENT.DELETE);

  const { data: templates = [], isFetching } = useVisaDocumentTemplates({ enabled: open });
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [fieldsTarget, setFieldsTarget] = useState(null); // { id, title }
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, title }

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setFile(null);
  };

  const { mutate: uploadTemplate, isLoading: uploading } = useUploadVisaDocumentTemplate({
    onSuccess: () => {
      toast.success("Document template uploaded");
      resetForm();
    },
    onError: (err) => toast.error(err?.response?.data?.errorMessage || "Failed to upload template"),
  });

  const { mutate: deleteTemplate, isLoading: deleting } = useDeleteVisaDocumentTemplate({
    onSuccess: () => {
      toast.success("Template removed");
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(err?.response?.data?.errorMessage || "Failed to remove template"),
  });

  const handleUpload = () => {
    if (!title.trim()) {
      toast.error("Enter a title for this document");
      return;
    }
    if (!file) {
      toast.error("Choose a PDF file");
      return;
    }
    uploadTemplate({ title: title.trim(), description: description.trim(), file });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="flex items-center justify-between">
        <span className="font-semibold">Visa document templates</span>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These PDFs appear as signable documents on every visa application's Documents tab. After
          uploading, use the map-fields icon to draw the fillable boxes in the Document Center —
          fields are no longer auto-detected from the PDF. Whether a document requires a MARA agent
          is also set there, per document.
        </Typography>

        {isFetching ? (
          <Box className="flex justify-center py-4">
            <CircularProgress size={22} />
          </Box>
        ) : templates.length === 0 ? (
          <Alert severity="info">No document templates uploaded yet.</Alert>
        ) : (
          <Stack spacing={1.25} sx={{ mb: canCreate ? 3 : 0 }}>
            {templates.map((template) => (
              <Paper key={template.id} variant="outlined" className="p-2.5 rounded-lg">
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Box className="min-w-0 flex-1">
                    <Typography variant="body2" fontWeight={700}>
                      {template.title}
                    </Typography>
                    {template.description && (
                      <Typography variant="caption" color="text.secondary" className="block">
                        {template.description}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }} flexWrap="wrap">
                      <Chip
                        size="small"
                        label={`${template.pageCount || 0} page(s)`}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                      <Chip
                        size="small"
                        label={`${template.fieldCount || 0} field(s)`}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                      {template.maraRequired && (
                        <Chip
                          size="small"
                          color="secondary"
                          label="Requires MARA"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      )}
                    </Stack>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() =>
                      navigate("/admin/documents", { state: { templateId: template.id } })
                    }
                    title="Map fields (Document Center)"
                  >
                    <EditLocationAltOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setFieldsTarget({ id: template.id, title: template.title })}
                    title="Rename / link fields"
                  >
                    <TuneOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    href={template.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                  {canDelete && (
                    <IconButton
                      size="small"
                      color="error"
                      disabled={deleting}
                      onClick={() => setDeleteTarget({ id: template.id, title: template.title })}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {canCreate && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Upload new template
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                label="Title"
                size="small"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Client Agreement Form"
              />
              <TextField
                label="Description (optional)"
                size="small"
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileOutlinedIcon fontSize="small" />}
                sx={{ justifyContent: "flex-start" }}
              >
                {file ? file.name : "Choose PDF file"}
                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </Button>
              <Button variant="contained" onClick={handleUpload} disabled={uploading}>
                {uploading ? "Uploading…" : "Upload template"}
              </Button>
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <VisaTemplateFieldsDialog
        open={Boolean(fieldsTarget)}
        onClose={() => setFieldsTarget(null)}
        templateId={fieldsTarget?.id}
        templateTitle={fieldsTarget?.title || ""}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove document template?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will remove "{deleteTarget?.title}" and it will no longer appear as a signable
            document on visa applications. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={() => deleteTemplate(deleteTarget.id)}
          >
            {deleting ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default VisaDocumentTemplateManagerDialog;
